import { useEffect, useRef, type FC } from 'react';

import { TextLogo } from '@livechat/design-system-icons';
import { Icon, NavigationTopBarAlert } from '@livechat/design-system-react-components';

import { isDesktopAppDetected } from 'helpers/desktop-app/is-detected';
import { openInCard } from 'helpers/routing';

import { LEGACY_MIGRATION_COPY, getLegacyMigrationTopbarMessage, LegacyMigrationEvent } from './constants';
import { downloadTextDesktopApp, getLegacyMigrationLandingUrl, trackLegacyMigrationEvent } from './helpers';
import { useMigrationDaysLeft } from './use-migration-days-left';

import { migrationAlert, migrationAlertCta, migrationAlertLinkCta, migrationAlertMessage } from './styles';

interface Props {
  isVisible: boolean;
}

export const LegacyMigrationBar: FC<Props> = ({ isVisible }) => {
  const wasVisible = useRef(false);

  useEffect(() => {
    if (isVisible && !wasVisible.current) {
      trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarDisplayed);
    }

    wasVisible.current = isVisible;
  }, [isVisible]);

  const handlePrimaryAction = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarCtaClicked);
    openInCard(getLegacyMigrationLandingUrl());
  };

  const handleDownloadTextApp = (): void => {
    trackLegacyMigrationEvent(LegacyMigrationEvent.TopbarDownloadTextAppClicked);
    downloadTextDesktopApp();
  };

  const daysLeft = useMigrationDaysLeft();

  return (
    <NavigationTopBarAlert
      kind="info"
      isVisible={isVisible}
      className={migrationAlert}
      primaryCta={{
        label: LEGACY_MIGRATION_COPY.primaryAction,
        onClick: handlePrimaryAction,
        size: 'compact',
        kind: 'secondary',
        className: migrationAlertCta,
      }}
      secondaryCta={
        isDesktopAppDetected()
          ? {
              label: LEGACY_MIGRATION_COPY.downloadTextApp,
              onClick: handleDownloadTextApp,
              size: 'compact',
              kind: 'link',
              className: migrationAlertLinkCta,
            }
          : undefined
      }
    >
      <span className={migrationAlertMessage}>
        <Icon source={TextLogo} size="medium" />
        {getLegacyMigrationTopbarMessage(daysLeft)}
      </span>
    </NavigationTopBarAlert>
  );
};
